import { useRef, useState } from 'react';
import styles from './LoginPage.module.css'
import ReCAPTCHA from 'react-google-recaptcha';
import authService from '../services/authService';
import { Link } from 'react-router-dom';

interface ForgotPasswordErrors {
    email?: string;
}

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [captchaValue, setCaptchaValue] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formErrors, setFormErrors] = useState<ForgotPasswordErrors>({});

    const handleCaptchaChange = (value: string | null): void => {
        setCaptchaValue(value);
        console.log('reCaptcha token:', value);
    };

    const handleCaptchaExpired = (): void => {
        setCaptchaValue(null);
        setError('reCAPTCHA expired. Please verify again.');
    };

    const handleCaptchaError = (): void => {
        setCaptchaValue(null);
        setError('reCAPTCHA error occurred. Please try again.');
    };

    const validateForm = () => {
        let errors: ForgotPasswordErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) {
            errors.email = "Email is required";
        }
        else if (!emailRegex.test(email)) {
            errors.email = "Please enter a valid email address";
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleResetPassword = () => {
        const resetPassword = async () => {
            try {
                const resetResult = await authService.forgotPassword(email, captchaValue!);
                if (resetResult === "Success") {
                    setSuccess('If your email is registered with us, you\'ll receive a password reset link shortly.');
                } else {
                    // Reset reCAPTCHA on failure
                    recaptchaRef.current?.reset();
                    setCaptchaValue(null);

                    if (resetResult === "EmailFailed") {
                        setError('Failed to send reset password email. Please check your email address.');
                    } else if (resetResult === "InvalidReCaptchaToken") {
                        setError('Invalid reCaptcha token.');
                    } else if (resetResult === "TooFrequent") {
                        setError('Too many requests. Please try again later.');
                    } else {
                        setError('An error occurred while processing your request. Please try again later.');
                    }
                }
            } catch (err) {
                recaptchaRef.current?.reset();
                setCaptchaValue(null);
                setError('An error occurred while processing your request. Please try again later.');
            } finally {
                setIsSubmitting(false);
            }
        };

        setError('');
        setSuccess('');

        if (validateForm()) {
            if (!captchaValue) {
                setError('Please complete the reCAPTCHA verification.');
                return;
            }
            setIsSubmitting(true);
            resetPassword();
        }
        
    };

    return (
        <>
            <div className='d-flex justify-content-lg-center'>
                <Link to="/"><img className={styles.loginLogo} src="/images/tahpi-plus.png" alt=""></img></Link>
            </div>
            <h3 className={` mb-4 text-center ${styles.title}`}>Forgot Password</h3>
            <div className={`${styles.formFloatingZindex} mb-3 form-floating`}>
                <input type="email" 
                 className={`form-control ${formErrors.email ? ` is-invalid` : ``}`}
                  placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)}></input>
                <label>Email address</label>
                {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
            </div>
            <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""} onChange={handleCaptchaChange} onExpired={handleCaptchaExpired} onError={handleCaptchaError} />
            {success && <div className="alert alert-success mt-2" role="alert">{success}</div>}
            {error && <div className="alert alert-danger mt-2" role="alert">{error}</div>}
            {!success && <button className="btn btn-lg btn-dark w-100 py-2 my-3 rounded-0" onClick={handleResetPassword} disabled={isSubmitting}>
                {isSubmitting ? (<><span className={`spinner-border ${styles.formSigninSpinner}`} role="status" aria-hidden="true"></span> Resetting password ...</>) : ("Reset Password")}
            </button>}
        </>
    );
}
export default ForgotPasswordPage;